type RedirectPathUser = {
  enterpriseId: string | null | undefined;
  hasAccessToDispensary: (dispensaryId: string) => boolean;
  isSuperAdmin: boolean;
  profile: { chainID?: string | null };
};

/**
 * Guards against blindly honoring a `redirectAfterLogin` path stashed in `localStorage` by
 * `PrivateRoute` before the user authenticated (see login.jsx).
 *
 * That key is a single global `localStorage` entry — it isn't scoped per user or session — so
 * on a shared/kiosk machine, or simply if it's stale from an earlier attempt, it can point at a
 * chain/dispensary/enterprise the user who *just* logged in has no relationship to. This
 * validates the stored path against the freshly-authenticated user's own access before it's
 * followed. Callers should fall back to the default `/redirect` landing route (which derives its
 * destination from the logged-in user's own profile) when this returns `false`.
 *
 * Only the three chain/dispensary/enterprise-scoped route prefixes are validated; every other
 * path (settings pages, top-level utility routes, etc.) isn't scoped to a specific tenant, so
 * there's nothing to validate ownership of.
 */
export function isAuthorizedRedirectPath(path: string, user: RedirectPathUser): boolean {
  if (!path || path === '/redirect') {
    return true;
  }

  if (user.isSuperAdmin) {
    // Dutchie staff are intentionally able to navigate into any chain/dispensary/enterprise.
    return true;
  }

  // PrivateRoute stashes `pathname + search` (see private.jsx), so `path` may carry a query
  // string. Match ownership against the pathname alone so a scoped route the user genuinely
  // owns isn't rejected just because it also has a `?...` on the end (e.g. `/chains/chain-1?tab=orders`).
  const [pathname] = path.split('?');

  const chainMatch = /^\/chains\/([^/]+)/.exec(pathname);
  if (chainMatch) {
    return Boolean(user.profile.chainID) && chainMatch[1] === user.profile.chainID;
  }

  const dispensaryMatch = /^\/dispensaries\/([^/]+)/.exec(pathname);
  if (dispensaryMatch) {
    return user.hasAccessToDispensary(dispensaryMatch[1]);
  }

  const enterpriseMatch = /^\/enterprise\/([^/]+)/.exec(pathname);
  if (enterpriseMatch) {
    return Boolean(user.enterpriseId) && enterpriseMatch[1] === user.enterpriseId;
  }

  return true;
}
